import { Controller } from "@hotwired/stimulus"

// Fomantic UI Calendar Controller - Design System v3
// 날짜/시간 선택을 위한 캘린더 컴포넌트
export default class extends Controller {
  static targets = ["calendar", "input", "clearButton"]

  static values = {
    type: { type: String, default: "date" },
    minDate: String,
    maxDate: String,
    firstDayOfWeek: { type: Number, default: 0 },
    disableYear: { type: Boolean, default: false },
    disableMonth: { type: Boolean, default: false }
  }

  connect() {
    if (typeof $ !== 'undefined' && $.fn && $.fn.calendar) {
      this.initializeCalendar()
    } else {
      this.waitForJQuery().then(() => {
        this.initializeCalendar()
      })
    }
  }

  disconnect() {
    if (typeof $ !== 'undefined' && $.fn && $.fn.calendar) {
      try {
        $(this.calendarTarget).calendar('destroy')
      } catch (e) {
        // ignore
      }
    }
  }

  waitForJQuery() {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (typeof $ !== 'undefined' && $.fn && $.fn.calendar) {
          clearInterval(checkInterval)
          resolve()
        }
      }, 50)

      setTimeout(() => {
        clearInterval(checkInterval)
        resolve()
      }, 5000)
    })
  }

  initializeCalendar() {
    if (typeof $ === 'undefined' || !$.fn || !$.fn.calendar) {
      return
    }

    const $calendar = $(this.calendarTarget)
    const self = this

    // 타입별 포맷 설정
    const formatters = {
      date: {
        type: 'date',
        formatter: {
          date: function(date, settings) {
            if (!date) return ''
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
          }
        }
      },
      month: {
        type: 'month',
        formatter: {
          month: function(date, settings) {
            if (!date) return ''
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            return `${year}-${month}`
          }
        }
      },
      year: {
        type: 'year',
        formatter: {
          year: function(date, settings) {
            if (!date) return ''
            return String(date.getFullYear())
          }
        }
      }
    }

    const typeConfig = formatters[this.typeValue] || formatters.date

    const options = {
      type: typeConfig.type,
      formatter: typeConfig.formatter,
      startMode: 'year',
      firstDayOfWeek: this.firstDayOfWeekValue,
      disableYear: this.disableYearValue,
      disableMonth: this.disableMonthValue,
      // 한국어 텍스트
      text: {
        days: ['일', '월', '화', '수', '목', '금', '토'],
        months: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
        monthsShort: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
        today: '오늘'
      },
      // 값 변경 시 hidden input 업데이트
      onChange: function(date, text, mode) {
        if (self.hasInputTarget) {
          self.inputTarget.value = text || ''
          // 폼 변경 이벤트 발생
          self.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
        }
        // clear 버튼 표시/숨김
        self.updateClearButton(!!date)
      }
    }

    // 최소/최대 날짜 설정
    if (this.hasMinDateValue && this.minDateValue) {
      options.minDate = this.parseDate(this.minDateValue)
    }
    if (this.hasMaxDateValue && this.maxDateValue) {
      options.maxDate = this.parseDate(this.maxDateValue)
    }

    $calendar.calendar(options)

    // 초기값이 있으면 설정
    if (this.hasInputTarget && this.inputTarget.value) {
      const initialDate = this.parseDate(this.inputTarget.value)
      if (initialDate) {
        $calendar.calendar('set date', initialDate, true, false)
      }
    }
  }

  parseDate(dateString) {
    if (!dateString) return null

    if (this.typeValue === 'month') {
      const [year, month] = dateString.split('-')
      return new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1)
    }

    if (this.typeValue === 'year') {
      return new Date(parseInt(dateString, 10), 0, 1)
    }

    // date
    return new Date(dateString)
  }

  // 프로그래밍 방식으로 날짜 설정
  setDate(date) {
    if (typeof $ !== 'undefined' && $.fn && $.fn.calendar) {
      $(this.calendarTarget).calendar('set date', date)
    }
  }

  // 날짜 초기화
  clear(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (typeof $ !== 'undefined' && $.fn && $.fn.calendar) {
      $(this.calendarTarget).calendar('clear')
      if (this.hasInputTarget) {
        this.inputTarget.value = ''
        this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
      }
      this.updateClearButton(false)
    }
  }

  // clear 버튼 표시/숨김
  updateClearButton(show) {
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.style.display = show ? '' : 'none'
    }
  }

  // 현재 날짜 가져오기
  getDate() {
    if (typeof $ !== 'undefined' && $.fn && $.fn.calendar) {
      return $(this.calendarTarget).calendar('get date')
    }
    return null
  }
}
